import { createAsyncThunk } from '@reduxjs/toolkit';

import config from '@/config';
import { GLSAxios } from '@/features/game/gls-axios';
import { tokenDecoder } from '@/features/geolocation/token-decoder';
import {
  GeolocationProduct,
  UpdateGeolocationPayload,
  UpdateGeolocationResponse,
  UpdateGeolocationTokenInput,
} from '@/features/geolocation-update-token/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const updateGeolocationToken = createAsyncThunk<
  Record<string, unknown> | undefined,
  UpdateGeolocationTokenInput
>('geolocationUpdateToken/updateGeolocationToken', async (input, thunkApi) => {
  try {
    Logger.rumAction({
      name: 'Begin update geolocation token',
      useCase: DdEventCategory.UpdateGeolocation,
      eventType: DdEventType.Info,
    });
    const axios = GLSAxios(input.fdState.toLocaleLowerCase());
    const gameLaunchUpdateUrl = `${config.fgp.FGPUpdatePath}/geolocationToken`;
    const tokenDetails = tokenDecoder(input.geolocationToken);

    const requestPayload: UpdateGeolocationPayload = {
      authToken: input.authToken,
      loginToken: input.loginToken,
      platform: input.platform,
      ipAddress: input.ipAddress,
      geolocationToken: input.geolocationToken,
      requestId: input.requestId,
      product: input.product as GeolocationProduct,
    };

    const updateGeolocationResponse =
      await axios.put<UpdateGeolocationResponse>(
        gameLaunchUpdateUrl,
        requestPayload,
      );

    if (updateGeolocationResponse.data.error) {
      WrapperProxy.setUpdateGeocomplyFailure();
      Logger.rumError({
        name: 'Failed to  update geolocation token',
        useCase: DdEventCategory.UpdateGeolocation,
        eventType: DdEventType.Error,
        context: {
          data: updateGeolocationResponse.data.data,
          tokenDetails,
        },
      });
      return thunkApi.rejectWithValue('UPDATE_TOKEN_REJECTED');
    }
    Logger.rumAction({
      name: 'Geolocation token update success',
      useCase: DdEventCategory.UpdateGeolocation,
      eventType: DdEventType.Success,
      context: {
        tokenDetails,
      },
    });

    WrapperProxy.setUpdateGeocomplySuccess();
    return updateGeolocationResponse.data as Record<string, unknown>;
  } catch (err) {
    WrapperProxy.setUpdateGeocomplyFailure();
    Logger.rumError({
      name: 'Geolocation token update error',
      useCase: DdEventCategory.UpdateGeolocation,
      eventType: DdEventType.Error,
      context: {
        error: err,
      },
    });
    return thunkApi.rejectWithValue('UPDATE_GEOLOCATION_TOKEN_ERROR');
  }
});
